// @ts-nocheck
/**
 * "Einplanen" on every tab: a bottom sheet to pick the day(s) and the meal for one recipe (styles:
 * assets/vhp-plan-picker.css, sheet: assets/vhp-sheet.js). Subscribers only.
 *  - Days and meals come from the planner's document in localStorage (assets/vhp-planner-likes.js): this week from
 *    today, next week when it is planned. Locked meals and days that already have the recipe are off.
 *  - Off the planner the placement waits in localStorage (QUEUE_KEY); the planner applies it on its next start with
 *    its own day picker logic (portion per day, leftovers, undo, save to the account). See take().
 *  - On the planner page the planner registers its own picker (setHandler): same tap, the full planner sheet.
 *  - Before: a jump to /pages/meal-planner#einplanen=<id> (the planner still reads that hash for old links).
 *
 *   window.vhpPlanPicker.open({ id, handle, title, image, slot, kcal, protein, carbs, fat, time }, { source })
 *   window.vhpPlanPicker.take()            // the planner: queued placements, removed from the queue
 *   window.vhpPlanPicker.setHandler(fn)    // the planner: fn(item) opens its own picker instead
 */
(() => {
  'use strict';

  const tr = window.vhpT; // snippets/vhp-i18n.liquid

  if (window.vhpPlanPicker) return;

  const QUEUE_KEY = 'vhp_plan_queue';
  const QUEUE_MAX = 20;
  const DAY_LABEL = tr.list('common.days_short');
  const DAY_NAME = tr.list('common.days_long');
  // Same occasions as the planner (EXAMPLE_OCCASIONS in assets/vhp-planner-funnel.js); catalog = recipe slot.
  const OCCASIONS = [
    { id: 'breakfast', catalog: 'breakfast', label: tr('common.slot_breakfast') },
    { id: 'lunch', catalog: 'main', label: tr('common.lunch') },
    { id: 'dinner', catalog: 'main', label: tr('common.dinner') },
    { id: 'snack', catalog: 'snack', label: tr('common.slot_snack') },
  ];
  const ISO_DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

  const likesApi = () => window.vhpPlannerLikes || null;
  const account = () => window.vhpAccount || null;
  const plannerUrl = () => (account()?.plannerUrl || '/pages/meal-planner').split('#')[0];
  const productId = (id) => likesApi()?.productId?.(id) || String(id ?? '').replace(/^.*\//, '');
  const esc = (value) =>
    String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  /* ---------------------------------------------------------------- dates (local, like the planner) */

  const pad = (n) => String(n).padStart(2, '0');
  const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const mondayOf = (offsetWeeks) => {
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + offsetWeeks * 7);
    return d;
  };
  const mondayIso = (offsetWeeks) => isoDate(mondayOf(offsetWeeks));
  const todayIndex = () => (new Date().getDay() + 6) % 7;

  /* ---------------------------------------------------------------- the planner's document */

  const readDoc = () => {
    try {
      return likesApi()?.readDoc?.() || JSON.parse(localStorage.getItem(likesApi()?.STORAGE_KEY || 'vhp_planner_anon') || 'null') || {};
    } catch (_) {
      return {};
    }
  };

  const hasPlan = (week) => Array.isArray(week) && week.some((day) => Array.isArray(day?.meals) && day.meals.length);

  // { start, week } for this week and next week. This week is offered even without a plan (the planner builds one);
  // next week only when it is planned. A week saved without a date is this week.
  const weeksFrom = (doc) => {
    const known = new Map();
    const add = (start, week) => {
      if (start && hasPlan(week) && !known.has(start)) known.set(start, week);
    };
    add(ISO_DAY_RE.test(String(doc.weekStart || '')) ? doc.weekStart : hasPlan(doc.week) ? mondayIso(0) : '', doc.week);
    add(doc.weekAlt?.start, doc.weekAlt?.week);
    const rows = [{ start: mondayIso(0), week: known.get(mondayIso(0)) || null, title: tr('picker.this_week') }];
    if (known.has(mondayIso(1))) rows.push({ start: mondayIso(1), week: known.get(mondayIso(1)), title: tr('picker.next_week') });
    return rows;
  };

  // The planner's meals: state.mealShares (Mahlzeiten screen, 0 = not in the plan) or, before that was set, its
  // slots (main always, breakfast and snack when chosen). Default: all of them.
  const occasionsFrom = (doc) => {
    const shares = doc.mealShares;
    if (shares && typeof shares === 'object' && OCCASIONS.some((row) => shares[row.id] > 0)) {
      return OCCASIONS.filter((row) => shares[row.id] > 0);
    }
    const slots = Array.isArray(doc.slots) && doc.slots.length ? doc.slots : ['breakfast', 'main', 'snack'];
    return OCCASIONS.filter((row) => row.catalog === 'main' || slots.includes(row.catalog));
  };

  /* ---------------------------------------------------------------- queue */

  const readQueue = () => {
    try {
      const list = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (_) {
      return [];
    }
  };

  const writeQueue = (list) => {
    try {
      if (list.length) localStorage.setItem(QUEUE_KEY, JSON.stringify(list.slice(-QUEUE_MAX)));
      else localStorage.removeItem(QUEUE_KEY);
      return true;
    } catch (_) {
      return false;
    }
  };

  const take = () => {
    const list = readQueue();
    if (list.length) writeQueue([]);
    return list;
  };

  /* ---------------------------------------------------------------- sheet */

  let sheet = null;
  let handler = null;
  // { item, source, weeks, occasions, as, targets: Set('start|day') }
  let pick = null;

  const build = () => {
    if (sheet) return;
    sheet = document.createElement('dialog');
    sheet.className = 'vhp-sheet vhp-pp';
    sheet.setAttribute('aria-labelledby', 'vhp-pp-title');
    sheet.innerHTML = `<div class="vhp-sheet__panel">
        <header class="vhp-sheet__head vhp-pp__head">
          <div class="vhp-sheet__handle" aria-hidden="true"></div>
          <h2 class="vhp-pp__title" id="vhp-pp-title" tabindex="-1">${tr('picker.title')}</h2>
          <p class="vhp-pp__recipe" data-pp-recipe></p>
        </header>
        <div class="vhp-pp__body" data-vhp-sheet-scroll>
          <p class="vhp-pp__label">${tr('picker.meal')}</p>
          <div class="vhp-pp__meals" role="radiogroup" aria-label="${tr('picker.meal')}" data-pp-meals></div>
          <div data-pp-weeks></div>
          <p class="vhp-pp__note" data-pp-note></p>
        </div>
        <div class="vhp-pp__foot">
          <button type="button" class="vhp-pp__confirm" data-pp-confirm disabled>${tr('picker.confirm')}</button>
        </div>
      </div>`;
    document.body.appendChild(sheet);
    sheet.addEventListener('click', onClick);
    sheet.addEventListener('click', (event) => {
      if (event.target === sheet) close();
    });
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });
    window.vhpSheet?.bindDrag(sheet, close);
  };

  const mealOf = (row, day, occasion) => row.week?.[day]?.meals?.find((meal) => meal.id === occasion) || null;

  // Why a day cannot take the recipe ('' = it can). Same rules as the planner's picker.
  const blockOf = (row, day) => {
    if (row.start === mondayIso(0) && day < todayIndex()) return 'past';
    if (!row.week) return '';
    const meal = mealOf(row, day, pick.as);
    if (!meal) return 'missing';
    if (productId(meal.pick?.id) === productId(pick.item.id)) return 'same';
    if (meal.locked) return 'locked';
    return '';
  };

  const prune = () => {
    const rows = new Map(pick.weeks.map((row) => [row.start, row]));
    pick.targets.forEach((key) => {
      const [start, day] = key.split('|');
      const row = rows.get(start);
      if (!row || blockOf(row, Number(day))) pick.targets.delete(key);
    });
  };

  const paint = () => {
    sheet.querySelector('[data-pp-recipe]').textContent = pick.item.title || tr('common.recipe');

    sheet.querySelector('[data-pp-meals]').innerHTML = pick.occasions
      .map(
        (row) =>
          `<button type="button" class="vhp-pp__chip" role="radio" aria-checked="${row.id === pick.as}" data-pp-as="${row.id}">${esc(row.label)}</button>`
      )
      .join('');

    const monday0 = mondayOf(0);
    sheet.querySelector('[data-pp-weeks]').innerHTML = pick.weeks
      .map((row) => {
        const monday = row.start === mondayIso(0) ? monday0 : mondayOf(1);
        const days = DAY_LABEL.map((label, day) => {
          const date = new Date(monday);
          date.setDate(monday.getDate() + day);
          const key = `${row.start}|${day}`;
          const block = blockOf(row, day);
          const on = !block && pick.targets.has(key);
          const why = block ? tr(`picker.why_${block}`) : '';
          const today = row.start === mondayIso(0) && day === todayIndex();
          return `<button type="button" class="vhp-pp__day${today ? ' is-today' : ''}${block === 'same' ? ' is-same' : ''}" role="checkbox"
            aria-checked="${on}" ${block ? `disabled aria-label="${DAY_NAME[day]} ${date.getDate()}, ${why}"` : `data-pp-day="${key}" aria-label="${DAY_NAME[day]} ${date.getDate()}"`}>
            <span class="vhp-pp__dow">${label}</span><span class="vhp-pp__date">${date.getDate()}</span>
          </button>`;
        }).join('');
        return `<section class="vhp-pp__week"><p class="vhp-pp__label">${esc(row.title)}</p><div class="vhp-pp__days" role="group" aria-label="${esc(row.title)}">${days}</div></section>`;
      })
      .join('');

    // One day picked: what the recipe replaces there.
    const note = sheet.querySelector('[data-pp-note]');
    const keys = Array.from(pick.targets);
    let text = '';
    if (keys.length === 1) {
      const [start, day] = keys[0].split('|');
      const row = pick.weeks.find((w) => w.start === start);
      const meal = row && mealOf(row, Number(day), pick.as);
      if (meal?.pick?.title) text = tr('picker.instead', { title: meal.pick.title });
    }
    if (!pick.weeks[0].week) text = tr('picker.creates_week');
    note.textContent = text;
    note.hidden = !text;

    const confirm = sheet.querySelector('[data-pp-confirm]');
    const n = pick.targets.size;
    confirm.disabled = !n;
    confirm.textContent = n > 1 ? tr('picker.confirm_n', { n }) : tr('picker.confirm');
  };

  const open = (item, opts = {}) => {
    if (!item?.id) return;
    const acc = account();
    // Only subscribers plan; everyone else lands on the planner (its paywall explains it).
    if (!acc?.subscriber) {
      window.location.href = plannerUrl();
      return;
    }
    window.vhpTrack?.('recipe_plan_add', { source: opts.source || '', subscriber: true, plannable: true, recipe_url: item.handle ? `/blogs/rezepte/${item.handle}` : '' });
    if (handler) {
      handler(item);
      return;
    }
    build();
    const doc = readDoc();
    const occasions = occasionsFrom(doc);
    pick = {
      item: { ...item, id: productId(item.id) },
      source: opts.source || '',
      weeks: weeksFrom(doc),
      occasions,
      as: (occasions.find((row) => row.catalog === item.slot) || occasions.find((row) => row.catalog === 'main'))?.id,
      targets: new Set(),
    };
    // Preselected: today, or the next open day this week.
    const here = pick.weeks[0];
    for (let day = todayIndex(); day < 7; day += 1) {
      if (!blockOf(here, day)) {
        pick.targets.add(`${here.start}|${day}`);
        break;
      }
    }
    paint();
    window.vhpSheet?.setOpen(sheet, true);
    sheet.querySelector('#vhp-pp-title')?.focus({ preventScroll: true });
  };

  const close = () => {
    if (sheet?.open) window.vhpSheet?.setOpen(sheet, false);
  };

  const confirm = () => {
    if (!pick?.targets.size) return;
    const item = pick.item;
    const entry = {
      id: item.id,
      handle: item.handle || '',
      title: item.title || '',
      image: item.image || '',
      slot: item.slot || '',
      kcal: item.kcal ?? null,
      protein: item.protein ?? null,
      carbs: item.carbs ?? null,
      fat: item.fat ?? null,
      time: item.time ?? null,
      occasion: pick.as,
      targets: Array.from(pick.targets),
      at: new Date().toISOString(),
    };
    const ok = writeQueue(readQueue().concat(entry));
    const n = entry.targets.length;
    const day = Number(entry.targets[0].split('|')[1]);
    const label = OCCASIONS.find((row) => row.id === entry.occasion)?.label || '';
    close();
    if (!ok) {
      toast(tr('picker.save_failed'));
      return;
    }
    window.vhpTrack?.('recipe_plan_queued', { source: pick.source, days: n, occasion: entry.occasion });
    document.dispatchEvent(new CustomEvent('vhp:plan-queued', { detail: entry }));
    toast(n === 1 ? tr('picker.planned_one', { day: DAY_LABEL[day], meal: label }) : tr('picker.planned_n', { n }));
  };

  function onClick(event) {
    const t = event.target;
    if (!(t instanceof Element) || !pick) return;
    const as = t.closest('[data-pp-as]');
    if (as) {
      pick.as = as.dataset.ppAs;
      prune();
      paint();
      return;
    }
    const day = t.closest('[data-pp-day]');
    if (day) {
      const key = day.dataset.ppDay;
      if (pick.targets.has(key)) pick.targets.delete(key);
      else pick.targets.add(key);
      paint();
      return;
    }
    if (t.closest('[data-pp-confirm]')) confirm();
  }

  /* ---------------------------------------------------------------- toast */

  let toastEl = null;
  let toastTimer = 0;
  const toast = (message) => {
    window.clearTimeout(toastTimer);
    toastEl?.remove();
    toastEl = document.createElement('div');
    toastEl.className = 'vhp-pp-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.innerHTML = `<span>${esc(message)}</span><a href="${esc(plannerUrl())}">${tr('common.to_plan')}</a>`;
    // Inside the top dialog under this sheet (reel, recipe sheet) so it is not under it.
    const host = Array.from(document.querySelectorAll('dialog[open]')).filter((d) => d !== sheet).pop() || document.body;
    host.appendChild(toastEl);
    window.requestAnimationFrame(() => toastEl?.classList.add('is-in'));
    toastTimer = window.setTimeout(() => {
      toastEl?.remove();
      toastEl = null;
    }, 4000);
  };

  window.vhpPlanPicker = {
    QUEUE_KEY,
    open,
    close,
    take,
    pending: () => readQueue().length,
    setHandler: (fn) => {
      handler = typeof fn === 'function' ? fn : null;
    },
  };
})();
